/**
 * @fileoverview Floating speed dial component for quick navigation to
 * user-configured pages. Renders on the right side of the screen, positioned
 * above any page-level FAB.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import { Architecture, CalendarMonth, CalendarToday, Dashboard, Description, FolderOpen, Home, Inbox, Link, People, Scale, Search, Settings, TableChart } from '@mui/icons-material';
import type { SpeedDialItem } from '@schema';
import { FAB_POSITION, SPEED_DIAL_BOTTOM } from '@/config/constants';
import { useSpeedDialItems } from '../hooks/useSpeedDialItems';

/** Curated map of MUI icon names to components. */
const ICON_MAP: Record<string, React.ComponentType> = {
  Architecture,
  CalendarMonth,
  CalendarToday,
  Dashboard,
  Description,
  FolderOpen,
  Home,
  Inbox,
  Link,
  People,
  Scale,
  Search,
  Settings,
  TableChart,
};

/** Resolves an icon name string to a MUI icon element, falling back to Link. */
function resolveIcon(iconName: string): React.ReactElement {
  const IconComponent = ICON_MAP[iconName] ?? Link;
  return <IconComponent />;
}

export function AppSpeedDial() {
  const { items, loading } = useSpeedDialItems();
  const navigate = useNavigate();

  const actions = useMemo(() =>
    items.map((item: SpeedDialItem) => ({
      id: item.id,
      name: item.label,
      icon: resolveIcon(item.icon_name),
      routePath: item.route_path,
    })),
    [items],
  );

  if (loading || actions.length === 0) {
    return null;
  }

  return (
    <SpeedDial
      ariaLabel="Quick navigation"
      sx={{
        position: 'fixed',
        bottom: SPEED_DIAL_BOTTOM,
        right: FAB_POSITION.RIGHT,
        '& .MuiFab-primary': { opacity: 0.5 },
      }}
      icon={<SpeedDialIcon />}
    >
      {actions.map((action) => (
        <SpeedDialAction
          key={action.id}
          icon={action.icon}
          slotProps={{ tooltip: { title: action.name } }}
          onClick={() => navigate(action.routePath)}
        />
      ))}
    </SpeedDial>
  );
}

